import { db } from './utils.mjs'
import {
  clearForceManualSignOnReconnect,
  clearWalletAuth,
  markForceManualSignOnReconnect,
  needsForcedManualSign,
} from './walletAuth.mjs'
import { clearWalletSession, walletSessionKey } from './walletSession.mjs'
import { detachTronWalletListeners, isTronInjectProvider } from './dapp/tronInject.mjs'
import { TRON_WC_WALLET_TYPE, isTronWalletConnectProvider } from './tronWcDetect.mjs'

export const WALLET_MANUAL_DISCONNECT_KEY = 'wallet_manual_disconnect'

export const WC_DISCONNECT_REASON = {
  user: { message: 'User disconnected.', code: 6000 },
  switch: { message: 'Switching wallet.', code: 6000 },
}

export function isManualDisconnect() {
  return db.get(WALLET_MANUAL_DISCONNECT_KEY) === '1'
}

export function markManualDisconnect() {
  db.set(WALLET_MANUAL_DISCONNECT_KEY, '1')
}

export function clearManualDisconnect() {
  db.set(WALLET_MANUAL_DISCONNECT_KEY, '')
}

/** 用户主动断开或签名失败后，禁止静默重连 */
export function blocksAutoReconnect() {
  return isManualDisconnect() || needsForcedManualSign()
}

export function markUserDisconnected() {
  markManualDisconnect()
  markForceManualSignOnReconnect()
}

export function clearReconnectBlock() {
  clearManualDisconnect()
  clearForceManualSignOnReconnect()
}

function isGlobalInjectedProvider(provider) {
  if (!provider || typeof window === 'undefined') return false
  return (
    provider === window.ethereum ||
    provider === window.bitkeep?.ethereum ||
    provider === window.tronWeb ||
    provider === window.tronLink?.tronWeb ||
    isTronInjectProvider(provider)
  )
}

export function isInjectedEip1193Provider(provider) {
  return isGlobalInjectedProvider(provider)
}

export function isWalletConnectProvider(provider) {
  return Boolean(provider && provider.isWalletConnect === true)
}

export function isEvmWalletConnectProvider(provider) {
  return isWalletConnectProvider(provider) && !isTronWalletConnectProvider(provider)
}

export function isTronWalletConnectMode(provider) {
  if (isTronWalletConnectProvider(provider)) return true
  return db.get('wallet_type', 'inject') === TRON_WC_WALLET_TYPE
}

/**
 * 以实际 provider 为准校正 wallet_type，避免「MetaMask 已连但 db 仍残留 wallet_connect」。
 * @returns {'inject'|'wallet_connect'}
 */
export function reconcileWalletType(provider) {
  const stored = db.get('wallet_type', 'inject')
  if (isInjectedEip1193Provider(provider)) {
    if (stored === 'wallet_connect' || stored === TRON_WC_WALLET_TYPE) {
      db.set('wallet_type', 'inject')
      db.set('topic', '')
    }
    return 'inject'
  }
  if (isTronWalletConnectProvider(provider)) {
    if (stored !== TRON_WC_WALLET_TYPE) {
      db.set('wallet_type', TRON_WC_WALLET_TYPE)
    }
    return TRON_WC_WALLET_TYPE
  }
  if (isWalletConnectProvider(provider) && !isTronWalletConnectProvider(provider)) {
    if (stored !== 'wallet_connect') {
      db.set('wallet_type', 'wallet_connect')
    }
    return 'wallet_connect'
  }
  if (stored === TRON_WC_WALLET_TYPE || stored === 'wallet_connect') {
    if (
      stored === 'wallet_connect'
      && typeof window !== 'undefined'
      && !isWalletConnectProvider(provider)
    ) {
      const eth = window.ethereum || window.bitkeep?.ethereum
      if (eth) {
        db.set('wallet_type', 'inject')
        db.set('topic', '')
        return 'inject'
      }
    }
    return stored === TRON_WC_WALLET_TYPE ? TRON_WC_WALLET_TYPE : 'wallet_connect'
  }
  return 'inject'
}

/** 当前会话是否应按 EVM WalletConnect 流程处理 */
export function isWalletConnectMode(provider) {
  if (isTronWalletConnectProvider(provider)) return false
  if (provider && typeof provider.request === 'function') {
    return reconcileWalletType(provider) === 'wallet_connect'
  }
  return reconcileWalletType(null) === 'wallet_connect'
}

export function detachProviderListeners(provider) {
  if (!provider || isGlobalInjectedProvider(provider)) return
  if (isWalletConnectProvider(provider)) return
  try {
    if (typeof provider.removeAllListeners === 'function') {
      provider.removeAllListeners()
    }
  } catch (_e) {
    void _e
  }
}

export function disconnectWalletConnect(provider, _reason) {
  const wt = db.get('wallet_type', 'inject')
  if (wt !== 'wallet_connect' && wt !== TRON_WC_WALLET_TYPE) return
  try {
    if (isTronWalletConnectProvider(provider)) {
      void provider._tronWallet?.disconnect?.()
      void import('./dapp/walletconnect-tron.mjs').then((m) => m.disposeActiveTronWalletConnect())
      return
    }
    if (wt === TRON_WC_WALLET_TYPE) {
      void import('./dapp/walletconnect-tron.mjs').then((m) => m.disposeActiveTronWalletConnect())
      return
    }
    if (typeof provider?.disconnect === 'function') {
      void provider.disconnect()
    }
  } catch (_e) {
    void _e
  }
}

/** 清理当前钱包连接（provider / topic / 登录态 / 可选 session） */
export function teardownWalletConnection(provider, options = {}) {
  const {
    wcReason = null,
    clearSession = false,
    markDisconnected = false,
    clearAuth = true,
  } = options

  detachTronWalletListeners()
  detachProviderListeners(provider)
  if (wcReason) disconnectWalletConnect(provider, wcReason)
  if (clearAuth) clearWalletAuth()
  db.set('topic', '')
  if (clearSession) clearWalletSession()
  if (markDisconnected) markUserDisconnected()
}

function currentWalletKey() {
  return String(walletSessionKey.value || db.get('wallet_key', '') || '').trim()
}

function currentWalletType() {
  return db.get('wallet_type', 'inject')
}

/** 选中新钱包前是否需要拆掉旧连接（同钱包重复选择 → false） */
export function shouldSwitchWallet({ account, nextWalletKey, nextWalletType }) {
  if (!account) return false
  const nextKey = String(nextWalletKey || '').trim()
  const sameWallet = currentWalletKey() === nextKey && currentWalletType() === nextWalletType
  return !sameWallet
}
